// Agreement page: the live usage dashboard of a deal, read at a glance. A
// seat deal shows its licenses against their use; a consumption deal its
// consumption against the commitment. The usage analysis on the project is
// a dated snapshot; this page is current. Fed by SEAT_DETAILS and
// CONSUMPTION_DETAILS, so another deal needs fixtures, not layout.

import { FC, ReactNode, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { clsx } from "clsx";
import {
    Button,
    Checkbox,
    DataTable,
    DataTableColumn,
    Empty,
    StatusDot,
    Tooltip,
    message,
} from "../../../../design-system";
import { VendorPage } from "../../../vendor/VendorPage";
import { useUserState } from "../../../../common/UserContext";
import {
    commitmentReachedMonth,
    commitmentYear,
    consumedToDate,
    consumptionOf,
    dealById,
    dealStatus,
    microsoftNumbers,
    effectiveProjects,
    overageOf,
    projectForDeal,
    projectedConsumption,
    unusedOf,
    vendorOfDeal,
} from "../model/derived";
import { CONSUMPTION_DETAILS, SEAT_DETAILS, isDrillableProject } from "../model/fixtures";
import {
    daysUntil,
    int,
    longDate,
    money,
    monthLabel,
    pct,
    relativeDays,
    shortDate,
} from "../model/format";
import { sendReminder, setPeopleState, useUsageDemoState } from "../model/store";
import type {
    FunctionCohort,
    PersonRow,
    PersonState,
    SkuRow,
    UsageDeal,
    UsageProject,
    WorkloadRow,
} from "../model/types";
import { usageProjectPath } from "../paths";
import { BurnDownChart, TrendChart } from "../components/charts";
import { DealStatusCell } from "../components/cells";
import { Field, HeaderCard, OwnerName } from "../components/HeaderCard";
import classes from "./usage.module.less";

const skuColumns: DataTableColumn<SkuRow>[] = [
    { key: "sku", title: "SKU", render: (r) => <strong>{r.sku}</strong> },
    {
        key: "bought",
        title: "Bought",
        align: "right",
        alwaysVisible: true,
        render: (r) => int(r.bought),
    },
    {
        key: "assigned",
        title: "Assigned",
        align: "right",
        alwaysVisible: true,
        render: (r) => int(r.assigned),
    },
    {
        key: "active",
        title: "Active 90d",
        align: "right",
        alwaysVisible: true,
        render: (r) => int(r.active90),
    },
    {
        key: "inactive",
        title: "Inactive",
        align: "right",
        alwaysVisible: true,
        render: (r) => int(r.inactive),
    },
    {
        key: "departed",
        title: "Departed",
        align: "right",
        alwaysVisible: true,
        render: (r) => int(r.departed),
    },
    {
        key: "risk",
        title: "Cost at risk",
        align: "right",
        alwaysVisible: true,
        render: (r) => money(r.costAtRisk),
    },
];

const workloadColumns: DataTableColumn<WorkloadRow>[] = [
    { key: "workload", title: "Workload", render: (r) => <strong>{r.workload}</strong> },
    {
        key: "share",
        title: "Share",
        align: "right",
        alwaysVisible: true,
        render: (r) => pct(r.sharePct),
    },
    {
        key: "annualized",
        title: "Annualized",
        align: "right",
        alwaysVisible: true,
        render: (r) => money(r.annualized),
    },
    {
        key: "since",
        title: "Since July",
        align: "right",
        alwaysVisible: true,
        render: (r) => (
            <span className={clsx(r.sinceJulyPct >= 30 && classes.overdue)}>
                {r.sinceJulyPct > 0 ? "+" : ""}
                {pct(r.sinceJulyPct)}
            </span>
        ),
    },
];

/**
 * A card's title with the button to the project's analysis, when the project
 * has a page: its usage analysis when one exists, its snapshot otherwise.
 */
const CardTitle: FC<{ title: string; project?: UsageProject }> = ({ title, project }) => {
    const navigate = useNavigate();
    const analysed = project?.analyses.some((a) => a.kind === "usage");
    const stage = analysed ? "?stage=usage" : "";
    return (
        <div className={classes.cardTitleRow}>
            <h3 className={classes.cardTitle}>{title}</h3>
            {project && isDrillableProject(project.id) && (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => navigate(`${usageProjectPath(project.id)}${stage}`)}
                >
                    Full analysis
                </Button>
            )}
        </div>
    );
};

const STATE_LABEL: Record<PersonState, string> = {
    awaiting_owner: "Awaiting owner",
    confirmed: "Confirmed",
    ready: "Ready",
    exempt: "Exempt",
    released: "Release at renewal",
    downgraded: "Downgrade to E3",
    keep: "Keep",
};

const STATE_COLOR: Record<PersonState, "grey" | "blue" | "orange" | "green" | "red"> = {
    awaiting_owner: "orange",
    confirmed: "green",
    ready: "blue",
    exempt: "grey",
    released: "blue",
    downgraded: "blue",
    keep: "green",
};

const ReviewStatus: FC<{ cohort: FunctionCohort }> = ({ cohort }) =>
    cohort.review.kind === "confirmed" ? (
        <StatusDot color="green">Confirmed {shortDate(cohort.review.on)}</StatusDot>
    ) : (
        <StatusDot color="orange">Due {shortDate(cohort.review.on)}</StatusDot>
    );

const SeatBody: FC<{ deal: UsageDeal; project?: UsageProject }> = ({ deal, project }) => {
    const detail = SEAT_DETAILS[deal.id];
    const state = useUsageDemoState();
    const [selected, setSelected] = useState<string[]>([]);
    const n = microsoftNumbers;
    const people = detail.people.map((p) => ({ ...p, state: state.peopleStates[p.id] ?? p.state }));
    const unitK = (amount: number) => `$${Math.round(amount / 1000)}K`;

    const apply = (next: PersonState, label: string) => {
        if (selected.length === 0) return;
        setPeopleState(selected, next);
        message.success(
            `${label}: ${selected.length} ${selected.length === 1 ? "person" : "people"} updated.`
        );
        setSelected([]);
    };
    const remind = (cohort: FunctionCohort) => {
        sendReminder(cohort.function);
        message.success(
            `Reminder sent to the ${cohort.function} owner for ${int(cohort.awaitingReview)} seats.`
        );
    };
    const toggle = (id: string, checked: boolean) =>
        setSelected((current) => (checked ? [...current, id] : current.filter((s) => s !== id)));

    const cohortColumns: DataTableColumn<FunctionCohort>[] = [
        { key: "function", title: "Function", render: (c) => <strong>{c.function}</strong> },
        {
            key: "active",
            title: "Active users",
            align: "right",
            alwaysVisible: true,
            render: (c) => int(c.activeUsers),
        },
        {
            key: "e3",
            title: "Fit E3",
            align: "right",
            alwaysVisible: true,
            render: (c) => int(c.fitE3),
        },
        {
            key: "e5",
            title: "Keep E5",
            align: "right",
            alwaysVisible: true,
            render: (c) => int(c.keepE5),
        },
        {
            key: "review",
            title: "Seats awaiting review",
            align: "right",
            alwaysVisible: true,
            render: (c) => (c.awaitingReview ? int(c.awaitingReview) : "–"),
        },
        {
            key: "status",
            title: "Owner review",
            alwaysVisible: true,
            render: (c) => <ReviewStatus cohort={c} />,
        },
        {
            key: "actions",
            title: "",
            actions: true,
            render: (c) =>
                c.review.kind === "due" ? (
                    <Button
                        size="small"
                        onClick={() => remind(c)}
                        disabled={!!state.remindersSent[c.function]}
                    >
                        {state.remindersSent[c.function] ? "Reminder sent" : "Send reminder"}
                    </Button>
                ) : null,
        },
    ];

    const peopleColumns: DataTableColumn<PersonRow>[] = [
        {
            key: "select",
            title: "",
            alwaysVisible: true,
            minWidth: 36,
            render: (p) => (
                <Checkbox
                    checked={selected.includes(p.id)}
                    onChange={(e) => toggle(p.id, e.target.checked)}
                    aria-label={`Select ${p.id}`}
                />
            ),
        },
        { key: "id", title: "Person", alwaysVisible: true, render: (p) => <code>{p.id}</code> },
        { key: "function", title: "Function", alwaysVisible: true, render: (p) => p.function },
        {
            key: "employment",
            title: "Employment",
            alwaysVisible: true,
            render: (p) => p.employment,
        },
        {
            key: "lastSignIn",
            title: "Last sign-in",
            alwaysVisible: true,
            render: (p) => longDate(p.lastSignIn),
        },
        {
            key: "inactive",
            title: "Days inactive",
            align: "right",
            alwaysVisible: true,
            render: (p) => int(p.daysInactive),
        },
        {
            key: "used",
            title: "Days used 30/60/90",
            align: "right",
            alwaysVisible: true,
            render: (p) =>
                p.daysUsed ? `${p.daysUsed.d30} / ${p.daysUsed.d60} / ${p.daysUsed.d90}` : "n/a",
        },
        {
            key: "features",
            title: "E5 features",
            align: "right",
            alwaysVisible: true,
            render: (p) => `${p.e5Features} of 5`,
        },
        {
            key: "rec",
            title: "Recommendation",
            alwaysVisible: true,
            render: (p) => p.recommendation,
        },
        {
            key: "state",
            title: "State",
            alwaysVisible: true,
            render: (p) => (
                <StatusDot color={STATE_COLOR[p.state]}>{STATE_LABEL[p.state]}</StatusDot>
            ),
        },
    ];

    return (
        <>
            <div className={clsx(classes.cards, classes["cards--two"])}>
                <div className={classes.card}>
                    <CardTitle title="Active users against licenses bought" project={project} />
                    <TrendChart points={detail.trend} />
                </div>
                <div className={classes.card}>
                    <h3 className={classes.cardTitle}>Licenses by SKU</h3>
                    <DataTable<SkuRow>
                        columns={skuColumns}
                        rows={detail.skus}
                        rowKey={(r) => r.sku}
                        sortable={false}
                        dense
                    />
                </div>
            </div>

            <div className={classes.card}>
                <h3 className={classes.cardTitle}>Where unused seats sit</h3>
                <DataTable<FunctionCohort>
                    columns={cohortColumns}
                    rows={detail.cohorts}
                    rowKey={(c) => c.function}
                    sortable={false}
                    dense
                />
            </div>

            <div className={classes.card}>
                <h3 className={classes.cardTitle}>People, {detail.renewalEffect.function}</h3>
                <div className={classes.chips}>
                    <span className={classes.chip}>
                        Ready to release <strong>{int(n.readyToRelease)}</strong> (
                        {int(detail.paidFor.neverAssigned)} never assigned,{" "}
                        {int(detail.used.departed)} departed)
                    </span>
                    <span className={classes.chip}>
                        Confirmed by owners <strong>{int(n.confirmedByOwners)}</strong>
                    </span>
                    <span className={classes.chip}>
                        Awaiting owner review <strong>{int(n.awaitingReview)}</strong>
                    </span>
                    <span className={classes.chip}>
                        Fit E3 <strong>{int(n.fitE3)}</strong>
                    </span>
                    <span className={classes.chip}>
                        Exempt <strong>{int(n.exempt)}</strong>
                    </span>
                </div>
                {selected.length > 0 && (
                    <div className={classes.bulkBar}>
                        <span className={classes.bulkBar__count}>{selected.length} selected</span>
                        <Button
                            size="small"
                            onClick={() => apply("awaiting_owner", "Owner review sent")}
                        >
                            Send owner review
                        </Button>
                        <Button
                            size="small"
                            onClick={() => apply("released", "Marked to release at renewal")}
                        >
                            Release at renewal
                        </Button>
                        <Button
                            size="small"
                            onClick={() => apply("downgraded", "Marked to downgrade to E3")}
                        >
                            Downgrade to E3
                        </Button>
                        <Button size="small" onClick={() => apply("keep", "Marked keep")}>
                            Mark keep
                        </Button>
                        <Button size="small" onClick={() => apply("exempt", "Exempted")}>
                            Exempt
                        </Button>
                    </div>
                )}
                <DataTable<PersonRow>
                    columns={peopleColumns}
                    rows={people}
                    rowKey={(p) => p.id}
                    sortable={false}
                    dense
                />
            </div>

            <div className={clsx(classes.cards, classes["cards--two"])}>
                <div className={classes.card}>
                    <h3 className={classes.cardTitle}>Reclamation rules</h3>
                    <ul className={classes.facts}>
                        {detail.rules.map((rule) => (
                            <li key={rule.label}>
                                <span className={classes.facts__label}>{rule.label}</span>
                                <span className={classes.facts__value}>{rule.value}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className={classes.card}>
                    <h3 className={classes.cardTitle}>
                        Effect on the renewal, {detail.renewalEffect.function} (
                        {int(detail.renewalEffect.seats)} seats)
                    </h3>
                    <div className={classes.effectRow}>
                        {(
                            [
                                ["If all release", detail.renewalEffect.allRelease],
                                ["If half release", detail.renewalEffect.halfRelease],
                                ["If none release", detail.renewalEffect.noneRelease],
                            ] as [string, number][]
                        ).map(([label, seats]) => (
                            <div key={label} className={classes.effectCell}>
                                <div className={classes.effectCell__label}>{label}</div>
                                <div className={classes.effectCell__value}>{int(seats)} E5</div>
                            </div>
                        ))}
                    </div>
                    <p className={classes.footnote}>
                        Each 100 seats released is {unitK(detail.renewalEffect.per100)} a year at
                        the current unit price.
                    </p>
                </div>
            </div>
        </>
    );
};

const ConsumptionBody: FC<{ deal: UsageDeal; project?: UsageProject }> = ({ deal, project }) => {
    const detail = CONSUMPTION_DETAILS[deal.id];
    const runRate = consumptionOf(deal) ?? 0;
    const reached = commitmentReachedMonth(deal);
    return (
        <>
            <div className={clsx(classes.cards, classes["cards--two"])}>
                <div className={classes.card}>
                    <CardTitle title="Consumption against the commitment" project={project} />
                    <BurnDownChart
                        monthly={detail.monthly}
                        commitment={deal.annualCost}
                        from={detail.monthly[0].month}
                        to={deal.term.renews.slice(0, 7)}
                        runRate={runRate}
                        reachedMonth={reached}
                    />
                </div>
                <div className={classes.card}>
                    <div className={classes.cardTitleRow}>
                        <h3 className={classes.cardTitle}>Where the consumption goes</h3>
                        <span className={classes.footnote}>Last 90 days, annualized</span>
                    </div>
                    <DataTable<WorkloadRow>
                        columns={workloadColumns}
                        rows={detail.workloads}
                        rowKey={(r) => r.workload}
                        sortable={false}
                        dense
                    />
                </div>
            </div>
        </>
    );
};

export const UsageDealView: FC = () => {
    const { dealId = "" } = useParams();
    const { me } = useUserState();
    const navigate = useNavigate();
    const state = useUsageDemoState();
    const projects = useMemo(() => effectiveProjects(state), [state]);

    const deal = dealById(dealId);
    if (!deal) {
        return (
            <VendorPage>
                <Empty description="No such deal in the demo." />
            </VendorPage>
        );
    }

    const vendor = vendorOfDeal(deal);
    const project = projectForDeal(deal.id, projects);
    const noticeOverdue = daysUntil(deal.term.noticeBy) < 0;
    const overage = overageOf(deal);
    const consumed = consumedToDate(deal);
    const year = commitmentYear(deal);
    const reached = commitmentReachedMonth(deal);
    const projected = projectedConsumption(deal);
    const body: ReactNode = SEAT_DETAILS[deal.id] ? (
        <SeatBody deal={deal} project={project} />
    ) : CONSUMPTION_DETAILS[deal.id] ? (
        <ConsumptionBody deal={deal} project={project} />
    ) : (
        <Empty description="Detail not part of this demo." />
    );

    return (
        <VendorPage>
            <HeaderCard
                vendor={vendor}
                logoFor={deal.name}
                name={deal.name}
                detail={vendor.name}
                actions={
                    project && isDrillableProject(project.id) ? (
                        <Button
                            type="primary"
                            onClick={() => navigate(usageProjectPath(project.id))}
                        >
                            Open project
                        </Button>
                    ) : undefined
                }
            >
                <Field label={deal.type === "consumption" ? "Commitment" : "Annual TCV"}>
                    {money(deal.annualCost)}
                </Field>
                {consumed != null && (
                    <Field label="Consumed to date">
                        <Tooltip
                            title={`${pct((consumed / deal.annualCost) * 100)} of the commitment at ${pct(year.share * 100)} of the term (${year.elapsed} of ${year.days} days)`}
                        >
                            <span>{money(consumed)}</span>
                        </Tooltip>
                    </Field>
                )}
                {deal.pacePct != null && (
                    <Field label="Pace, last 90 days">
                        <Tooltip
                            title={`${money(consumptionOf(deal) ?? 0)} a year${reached ? `; commitment reached in ${monthLabel(reached)}` : ""}`}
                        >
                            <span className={clsx(deal.pacePct > 100 && classes.overdue)}>
                                {pct(deal.pacePct)}
                            </span>
                        </Tooltip>
                    </Field>
                )}
                <Field label={overage > 0 ? "Projected overage" : "Unused capacity"}>
                    <Tooltip
                        title={
                            overage > 0 && projected != null
                                ? `${money(projected - deal.annualCost)} above the commitment, billed at the on-demand rate, ${deal.commitment?.onDemandPremiumPct ?? 0}% higher`
                                : undefined
                        }
                    >
                        <span className={classes.overdue}>
                            {money(overage > 0 ? overage : unusedOf(deal))}
                        </span>
                    </Tooltip>
                </Field>
                <Field label="Owner">
                    <OwnerName name={me.name} />
                </Field>
                <Field label="Status">
                    <DealStatusCell
                        status={dealStatus(deal, projects)}
                        project={project}
                        linked={false}
                    />
                </Field>
                <Field label="Due">
                    <Tooltip title={`Notice by ${longDate(deal.term.noticeBy)}`}>
                        <span className={clsx(noticeOverdue && classes.overdue)}>
                            {relativeDays(deal.term.noticeBy).replace("in ", "")}
                        </span>
                    </Tooltip>
                </Field>
            </HeaderCard>
            {body}
        </VendorPage>
    );
};
